import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { HiOutlineChartBar } from 'react-icons/hi2';
import { useSelector } from "react-redux";
import {
  DashboardStatus,
  dashboardStatusHexColors,
  getDashboardStatusColor,
  getDashboardStatusLabel
} from "../../../../constants/integrationTypes";
import { useJwt } from "../../../../helpers/common";
import { StrategyEntityItemType } from '../../../../services/strategyMap';
import TooltipWrapper from '../../../../shared/atoms/TooltipWrapper';
import { getEntityTypeDisplay } from '../../../../utils/entityDisplayUtils';
import { hasAnyPositiveImpact } from "../../../../utils/taskUtils";
import moment from 'moment';
import { useResourceInfo } from '../../../../hooks/useResourceInfo';
import CustomTooltip from '../../../../components/common/CustomTooltip';
import { EntityViewTooltip } from '../../../../features/EntityViewTooltip/EntityViewTooltip';
import './StrategyMapSidePanelItem.css';
import { FaGlasses } from 'react-icons/fa';

interface Props {
  projectData: StrategyEntityItemType;
  isSimulation: boolean;
  onItemClick?: () => void;
  onTimelineClick?: () => void;
  onContextMenu?: (e: React.MouseEvent, id: string) => void;
  isHighlighted?: boolean;
}

const StrategyMapSidePanelItem: React.FC<Props> = ({
  projectData,
  isSimulation,
  onItemClick,
  onTimelineClick,
  onContextMenu,
  isHighlighted = false,
}) => {
  const jwtData: any = useJwt();
  const isAdmin = typeof jwtData?.org_role === 'string' && jwtData?.org_role?.toLowerCase() === 'admin';
  const { show_cost } = useSelector((state: any) => state.organization.orgDetails);

  const itemRef = React.useRef<HTMLLIElement>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const hideTimerRef = React.useRef<number | null>(null);
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
  const [arrowPos, setArrowPos] = React.useState<{ top: number; side: 'left' | 'right' }>({ top: 0, side: 'right' });
  const { getResourceName } = useResourceInfo();

  const {
    id: entity_id,
    name: entity_name,
    entity_type,
    child_entity_types,
    status,
    sim_data,
    dependencies = [],
    progress,
    is_sim_pending,
  } = projectData;

  const currentStatus = isSimulation ? (sim_data?.status || status) : status;
  const statusKey = currentStatus?.toLowerCase() || '';
  const statusLabel = getDashboardStatusLabel(statusKey);
  const statusHexColor = dashboardStatusHexColors[statusKey] || '#a3a3a3';

  const showRiskTriangle = [DashboardStatus.AT_RISK, DashboardStatus.DELAYED, DashboardStatus.UNALLOCATED, DashboardStatus.BLOCKED].includes(statusKey as DashboardStatus);

  useEffect(() => {
    if (isHighlighted && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [isHighlighted]);

  const entityInfo = getEntityTypeDisplay(entity_type, projectData?.type);
  const ShowIcon = entityInfo.icon;

  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));
  const TOOLTIP_WIDTH = 300;
  const VIEWPORT_MARGIN = 12;
  const ARROW_SIZE = 12;
  const calculateTooltipPosition = React.useCallback(() => {
    if (!itemRef.current) return;
    const rect = itemRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const rightCandidate = rect.right + VIEWPORT_MARGIN;
    const placeOnRight = rightCandidate + TOOLTIP_WIDTH + VIEWPORT_MARGIN <= vw;
    const left = placeOnRight
      ? clamp(rightCandidate, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN)
      : clamp(rect.left - TOOLTIP_WIDTH - VIEWPORT_MARGIN, VIEWPORT_MARGIN, vw - TOOLTIP_WIDTH - VIEWPORT_MARGIN);

    const provisionalTop = rect.top + rect.height / 2;
    setTooltipPos({ left, top: provisionalTop });

    requestAnimationFrame(() => {
      const el = tooltipRef.current;
      if (!el) return;
      const th = el.offsetHeight || 200;
      const centeredTop = rect.top + rect.height / 2 - th / 2;
      const top = clamp(centeredTop, VIEWPORT_MARGIN, vh - th - VIEWPORT_MARGIN);
      setTooltipPos(prev => ({ ...prev, top }));

      const itemCenterY = rect.top + rect.height / 2;
      const arrowTop = clamp(itemCenterY - top - ARROW_SIZE / 2, 8, th - ARROW_SIZE - 8);
      setArrowPos({ top: arrowTop, side: placeOnRight ? 'left' : 'right' });
    });
  }, []);

  // Listen for close event from other tooltips
  useEffect(() => {
    const handleCloseOtherTooltips = (e: CustomEvent) => {
      if (e.detail?.id !== entity_id) {
        setShowTooltip(false);
        if (hideTimerRef.current) {
          window.clearTimeout(hideTimerRef.current);
          hideTimerRef.current = null;
        }
      }
    };

    window.addEventListener('CLOSE_STRATEGY_MAP_TOOLTIPS' as any, handleCloseOtherTooltips as any);
    return () => {
      window.removeEventListener('CLOSE_STRATEGY_MAP_TOOLTIPS' as any, handleCloseOtherTooltips as any);
    };
  }, [entity_id]);

  const handleMouseEnter = () => {
    // Dispatch event to close other tooltips immediately
    const event = new CustomEvent('CLOSE_STRATEGY_MAP_TOOLTIPS', { detail: { id: entity_id } });
    window.dispatchEvent(event);

    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    setShowTooltip(true);
    setTimeout(calculateTooltipPosition, 0);
  };

  const handleMouseLeave = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
    }
    // Small delay to allow moving into tooltip
    hideTimerRef.current = window.setTimeout(() => {
      setShowTooltip(false);
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    if (!showTooltip) return;
    const onResize = () => calculateTooltipPosition();
    const onScroll = () => setShowTooltip(false);
    
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showTooltip, calculateTooltipPosition]);

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedKey(key);
        window.setTimeout(() => setCopiedKey(null), 1500);
      } catch (_) { }
    }
  };

  const CopyIcon = ({ isCopied = false }: { isCopied?: boolean }) => (
    isCopied ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )
  );

  // Removed renderTooltipContent as it's now EntityViewTooltip
  
  return (
    <>
      <li
        ref={itemRef}
        className={`smap-side-panel-item ${isHighlighted ? "smap-side-panel-item--highlighted" : ""}`}
        onClick={onItemClick}
        onContextMenu={(e) => onContextMenu?.(e, entity_id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex items-center gap-1 mr-2 min-w-0 w-full">
          <div className="flex items-center relative mr-1 flex-shrink-0">
            {/* @ts-ignore */}
            <ShowIcon size={18} className={entityInfo.iconClass} />
            <span
              className={`smap-side-panel-item__status-dot ${getDashboardStatusColor(statusKey)}`}
            />
          </div>
          <span
            className={`${showRiskTriangle ? "text-red-500" : ""} smap-side-panel-item__name truncate`}
          >
            {entity_name}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {dependencies.length > 0 && (
            <div className="flex w-3 h-fit">
              <img
                src={
                  hasAnyPositiveImpact(dependencies, isSimulation)
                    ? "/configuration%20red.svg"
                    : "/configuration%20green.svg"
                }
                alt="Dependency"
                className="w-3 h-3"
              />
            </div>
          )}

          {showRiskTriangle && (
            <FiAlertTriangle className="smap-side-panel-item-icon smap-side-panel-item__risk-icon" />
          )}
          {is_sim_pending && (
            <FaGlasses className="text-[#467966] smap-side-panel-item-icon smap-side-panel-item__risk-icon" />
          )}
        </div>
        <div className="smap-side-panel-item__timeline-icon ">
          <div className="flex gap-2 items-center rounded-full bg-gray-200 dark:bg-gray-800 px-3 py-1">
            <HiOutlineChartBar
              className="text-xs cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onTimelineClick?.();
              }}
            />
          </div>
        </div>
      </li>

      {showTooltip &&
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            ref={tooltipRef}
            className={`z-[9999] w-[300px] shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg fixed`}
            style={{
              left: tooltipPos.left,
              top: tooltipPos.top,
              // Initial opacity transition could be added here if needed, but wasn't in original logic
            }}
            onMouseEnter={() => {
              if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              if (hideTimerRef.current) {
                window.clearTimeout(hideTimerRef.current);
              }
              hideTimerRef.current = window.setTimeout(() => {
                setShowTooltip(false);
                hideTimerRef.current = null;
              }, 200); // Same grace period
            }}
          >
            <div
              className="pointer-events-none absolute"
              style={{
                top: arrowPos.top,
                left: arrowPos.side === "left" ? -6 : undefined,
                right: arrowPos.side === "right" ? -6 : undefined,
              }}
            >
              <div className="w-3 h-3 rotate-45 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700" />
            </div>
            {/* {renderTooltipContent()} */}
            <EntityViewTooltip projectData={projectData} isSimulation={isSimulation} />
          </div>,
          document.body,
        )}
    </>
  );
};

export default StrategyMapSidePanelItem;